import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Alert, AlertTitle, Box, Button, Link, Paper, Snackbar, Stack, Typography } from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash'
import { useAdminRestaurarConfiguracaoMutation, type ConfiguracaoLoja } from '../../../api'
import { DialogConfirmar } from '../../../components/admin/dialog-confirmar'
import { DialogExcluirLoja, TEXTO_EXCLUSAO_LOJA } from '../../../components/admin/dialog-excluir-loja'
import { formatarData } from '../../../lib/formato'
import { estadoLojaExcluida } from '../../../lib/lojas-admin'
import { useAcaoUnica } from '../../../lib/use-acao-unica'

type LojaExclusao = Pick<ConfiguracaoLoja, 'id' | 'nome' | 'ativo' | 'dataExclusao' | 'usuarioExclusao'>

interface SecaoExcluirCardapioProps {
  loja: Pick<ConfiguracaoLoja, 'id' | 'nome'>
  podeExcluir: boolean
}

export function SecaoExcluirCardapio({ loja, podeExcluir }: SecaoExcluirCardapioProps): JSX.Element {
  const navigate = useNavigate()
  const [confirmando, setConfirmando] = useState(false)

  // Sem limpar a seleção: o /admin reconcilia pela lista nova (o suporte continua na excluída, vendo a faixa).
  function aoExcluir(): void {
    setConfirmando(false)
    navigate('/admin', { replace: true, state: estadoLojaExcluida(loja) })
  }

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        p: { xs: 2.5, md: 4 },
        borderColor: podeExcluir ? alpha(t.palette.error.main, 0.4) : 'divider',
      })}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" color={podeExcluir ? 'error' : 'text.primary'} sx={{ lineHeight: 1.2 }}>
            Excluir loja
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {podeExcluir
              ? TEXTO_EXCLUSAO_LOJA
              : 'Só o supervisor da empresa (quem gerencia os usuários no Sischef) ou o suporte Sischef pode excluir esta loja. Peça a um deles.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Quer só fechar por uns dias? Use <strong>Pausar agora</strong> em{' '}
            <Link component={RouterLink} to="/admin/horarios">
              Horários de funcionamento
            </Link>{' '}
            — lá você reativa quando quiser.
          </Typography>
        </Box>
        {podeExcluir && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setConfirmando(true)}
            sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
          >
            Excluir loja
          </Button>
        )}
      </Stack>

      {podeExcluir && (
        <DialogExcluirLoja
          aberto={confirmando}
          loja={loja}
          onFechar={() => setConfirmando(false)}
          onExcluida={aoExcluir}
        />
      )}
    </Paper>
  )
}

const MENSAGEM_ERRO_RESTAURAR: Record<string, string> = {
  SOMENTE_SUPORTE: 'Só o suporte Sischef pode restaurar uma loja excluída.',
  NAO_ENCONTRADO: 'Esta loja não foi encontrada.',
}

function mensagemErroRestaurar(erro: unknown): string {
  const dados = (erro as { data?: { codigo?: unknown; mensagem?: unknown } } | undefined)?.data
  const padrao = typeof dados?.codigo === 'string' ? MENSAGEM_ERRO_RESTAURAR[dados.codigo] : undefined
  if (!padrao) return 'Não foi possível restaurar a loja. Tente novamente.'
  return typeof dados?.mensagem === 'string' && dados.mensagem ? dados.mensagem : padrao
}

function descreverExclusao(loja: LojaExclusao): string {
  const quando = loja.dataExclusao
    ? ` em ${formatarData(loja.dataExclusao, 'data')} ${formatarData(loja.dataExclusao, 'hora')}`
    : ''
  const quem = loja.usuarioExclusao ? ` por ${loja.usuarioExclusao}` : ''
  return `Excluída${quando}${quem}. Está fora do ar para os clientes e só o suporte vê esta loja.`
}

// Fixa só a partir de `sm` (AppBar de 64px); no celular a faixa rola junto com a página.
export function estiloFixoNoTopo(t: Theme): SystemStyleObject<Theme> {
  return {
    [t.breakpoints.up('sm')]: { position: 'sticky', top: 64, zIndex: t.zIndex.drawer - 2 },
  }
}

// Fica montado depois de restaurar (o aviso dos endereços sobrevive ao refetch); o pai remonta por loja com `key`.
export function AvisoCardapioExcluido({ loja }: { loja: LojaExclusao }): JSX.Element | null {
  const [restaurar] = useAdminRestaurarConfiguracaoMutation()
  const [confirmando, setConfirmando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [enderecosPerdidos, setEnderecosPerdidos] = useState<string[]>([])
  const [restaurado, setRestaurado] = useState(false)
  const [restauradaNestaTela, setRestauradaNestaTela] = useState(false)

  const [confirmarRestauracao, restaurando] = useAcaoUnica(async () => {
    setErro(null)
    try {
      const resposta = await restaurar(loja.id).unwrap()
      setEnderecosPerdidos(resposta.enderecosNaoRecuperados ?? [])
      setRestaurado(true)
      setRestauradaNestaTela(true)
    } catch (e) {
      setErro(mensagemErroRestaurar(e))
    } finally {
      setConfirmando(false)
    }
  })

  return (
    <>
      {!loja.ativo && !restauradaNestaTela && (
        <Alert
          variant="filled"
          severity="error"
          icon={<DeleteOutlineIcon fontSize="inherit" />}
          sx={(t) => ({
            ...estiloFixoNoTopo(t),
            mb: 3,
            alignItems: 'flex-start',
            '& .MuiAlert-message': { flexGrow: 1 },
          })}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>Loja excluída</AlertTitle>
              {descreverExclusao(loja)}
            </Box>
            <Button
              variant="contained"
              startIcon={<RestoreFromTrashIcon />}
              onClick={() => setConfirmando(true)}
              disabled={restaurando}
              disableElevation
              sx={{
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                bgcolor: 'common.white',
                color: 'error.dark',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              Restaurar
            </Button>
          </Stack>
        </Alert>
      )}

      {erro && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      {/* Sem botão de fechar: restaurar de novo devolve a lista vazia, então esta é a única vez que ela aparece. */}
      {enderecosPerdidos.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Alguns endereços não voltaram</AlertTitle>
          Outra loja passou a usar estes endereços — quem abrir esses links agora cai na outra loja:
          <Box component="ul" sx={{ my: 1, pl: 3, wordBreak: 'break-all' }}>
            {enderecosPerdidos.map((endereco) => (
              <li key={endereco}>{endereco}</li>
            ))}
          </Box>
          Avise o lojista: cadastre um endereço novo em <strong>Delivery</strong> ou <strong>Vitrine</strong> ›{' '}
          <strong>Gerenciar pontos de acesso</strong> e troque o link onde ele divulgou (Instagram, QR impresso).
        </Alert>
      )}

      <DialogConfirmar
        aberto={confirmando}
        titulo={`Restaurar a loja ${loja.nome}?`}
        descricao="A loja volta para a lista de lojas e para o ar. Endereço que outra loja passou a usar não volta."
        textoConfirmar="Restaurar"
        destrutivo={false}
        processando={restaurando}
        aoConfirmar={() => void confirmarRestauracao()}
        aoCancelar={() => setConfirmando(false)}
      />

      <Snackbar
        open={restaurado}
        autoHideDuration={3000}
        onClose={() => setRestaurado(false)}
        message="Loja restaurada"
      />
    </>
  )
}
